/**
 * Product Inventory Component
 * Uses local metafield data - NO API calls
 *
 * Version: 3.1
 *
 * Changes in 3.1:
 * - Removed duplicate translation logic - delegates to CustomInventoryAPI (DRY principle)
 *
 * Changes in 3.0:
 * - Removed all Worker API calls
 * - Reads stock data from Liquid-rendered JSON (own_stock, external_stock from metafields)
 * - Uses CustomInventoryAPI.getStockStatus() for status calculation only
 */

/**
 * Translate delivery time to current locale
 * Delegates to CustomInventoryAPI if available (loaded by custom-inventory-local.js)
 */
function translateDeliveryTime(deliveryTime) {
  if (!deliveryTime) return '';

  // Delegate to CustomInventoryAPI if available
  if (window.CustomInventoryAPI?.translateDeliveryTime) {
    return window.CustomInventoryAPI.translateDeliveryTime(deliveryTime);
  }

  // Fallback: return as-is if API not loaded
  return deliveryTime;
}

if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      // Custom attributes
      this.isExtendedAssortment = this.dataset.isExtendedAssortment === 'true';
      this.isBundle = this.dataset.isBundle === 'true';
      this.productId = this.dataset.productId;

      // Bind to on:variant:change. The event bubbles from <variant-picker>, so we
      // listen on the nearest product section rather than the picker itself. This
      // keeps the main instance working AND lets a second instance (e.g. inside the
      // sticky ATC bar, which lives outside .product-info) stay in sync without
      // crashing on a null .product-info lookup.
      const eventScope = this.closest('.cc-main-product') || this.closest('.product-info') || document;
      eventScope.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      // Initialize with data from DOM
      this.initInventory();
    }

    /**
     * Initialize inventory with data from DOM attributes
     */
    initInventory() {
      const isBundle = this.isBundle;
      const variantAvailable = this.dataset.variantAvailable === 'true';

      // Bundle products: use Shopify's native availability directly
      if (isBundle) {
        const status = this.getStockStatus({
          ownStock: 0,
          externalStock: 0,
          isBundle: true,
          variantAvailable: variantAvailable
        });
        this.updateDisplay(status);
        return;
      }

      // Read stock data from DOM attributes (rendered by Liquid from metafields)
      const ownStock = parseInt(this.dataset.ownStock, 10) || 0;
      const externalStock = parseInt(this.dataset.externalStock, 10) || 0;
      const inventoryPolicy = this.dataset.inventoryPolicy || 'deny';
      const deliveryTime = this.dataset.deliveryTime || '';

      const status = this.getStockStatus({
        ownStock,
        externalStock,
        inventoryPolicy,
        deliveryTime,
        isExtendedAssortment: this.isExtendedAssortment,
        isBundle: false,
        variantAvailable
      });

      this.updateDisplay(status);
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      if (!dataEl) return [];
      try {
        return JSON.parse(dataEl.textContent);
      } catch (e) {
        return [];
      }
    }

    /**
     * Calculate stock status - uses CustomInventoryAPI if available, otherwise local logic
     */
    getStockStatus(params) {
      // Use CustomInventoryAPI if available (from custom-inventory-local.js)
      if (window.CustomInventoryAPI && typeof window.CustomInventoryAPI.getStockStatus === 'function') {
        return window.CustomInventoryAPI.getStockStatus(params);
      }

      // Fallback local logic
      const { ownStock, externalStock, inventoryPolicy, deliveryTime, isExtendedAssortment, isBundle, variantAvailable } = params;
      const t = (key) => window.InventoryTranslations?.[key] || key;

      if (isBundle) {
        if (variantAvailable) {
          return { level: 'in_stock', statusText: t('inStock'), urgencyText: '', isAvailable: true };
        } else {
          return { level: 'none', statusText: t('noStock'), urgencyText: '', isAvailable: false };
        }
      }

      if (isExtendedAssortment) {
        if (ownStock > 0) {
          return { level: 'in_stock', statusText: t('inStock'), urgencyText: '', isAvailable: true };
        } else if (externalStock > 0) {
          if (deliveryTime) {
            const translatedDeliveryTime = translateDeliveryTime(deliveryTime);
            return { level: 'extended_in_stock', statusText: t('orderProductDelivery'), urgencyText: translatedDeliveryTime, isAvailable: true };
          } else {
            return { level: 'extended_in_stock', statusText: t('orderProduct'), urgencyText: '', isAvailable: true };
          }
        } else {
          return { level: 'none', statusText: t('temporarilyOut'), urgencyText: '', isAvailable: false };
        }
      } else {
        if (ownStock > 0) {
          return { level: 'in_stock', statusText: t('inStock'), urgencyText: '', isAvailable: true };
        } else if (inventoryPolicy === 'continue') {
          const translatedDeliveryTime = deliveryTime ? translateDeliveryTime(deliveryTime) : t('deliveryConfirmed');
          return { level: 'backordered', statusText: translatedDeliveryTime, urgencyText: '', isAvailable: true };
        } else {
          return { level: 'none', statusText: t('temporarilyOut'), urgencyText: '', isAvailable: false };
        }
      }
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const variant = evt.detail.variant;
        const inventoryData = this.variantInventory.find(v => v.id === variant.id);

        if (!inventoryData) {
          this.hidden = true;
          return;
        }

        // Bundle products: use Shopify's native availability
        if (this.isBundle) {
          const status = this.getStockStatus({
            ownStock: 0,
            externalStock: 0,
            isBundle: true,
            variantAvailable: inventoryData.available
          });
          this.updateDisplay(status);
          return;
        }

        // Read stock from local JSON data (rendered by Liquid from metafields)
        // Important: preserve explicit zero values from Liquid JSON.
        // Using `||` here causes own_stock=0 to fall back to inventory_quantity,
        // which incorrectly marks order-only variants as "in stock".
        const ownStock = inventoryData.own_stock ?? inventoryData.inventory_quantity ?? 0;
        const externalStock = inventoryData.external_stock ?? 0;
        const inventoryPolicy = inventoryData.inventory_policy ?? 'deny';
        const deliveryTime = inventoryData.delivery_time ?? '';

        const status = this.getStockStatus({
          ownStock,
          externalStock,
          inventoryPolicy,
          deliveryTime,
          isExtendedAssortment: this.isExtendedAssortment,
          isBundle: false,
          variantAvailable: inventoryData.available
        });

        this.updateDisplay(status);
      } else {
        this.hidden = true;
      }
    }

    /**
     * Updates the display based on stock status
     * @param {Object} status - { level, statusText, urgencyText, isAvailable }
     */
    updateDisplay(status) {
      const { level, statusText, urgencyText } = status;

      // Update DOM
      this.hidden = false;
      this.setAttribute('data-inventory-level', level);

      if (this.inventoryNotice) {
        this.inventoryNotice.innerText = statusText;
      }

      if (this.urgencyMessage) {
        // No leading space needed - locale text already has trailing space
        this.urgencyMessage.innerText = urgencyText || '';

        // Debug logging (remove after testing)
        if (window.location.search.includes('debug=inventory')) {
          console.log('[ProductInventory] Setting urgency text:', {
            urgencyText: urgencyText,
            element: this.urgencyMessage
          });
        }
      }

      // Update indicator bar if present
      if (this.indicatorBar) {
        if (level === 'none' || level === 'backordered') {
          this.indicatorBar.hidden = true;
        } else {
          this.indicatorBar.hidden = false;
        }
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
